import ScreenRouter from './engine/ScreenRouter.js';
import AudioManager from './engine/AudioManager.js';
import Game from './game/game.js';
import MenuScreen from './ui/MenuScreen.js';
import CatalogScreen from './ui/CatalogScreen.js';
import Hud from './ui/Hud.js';
import ResultsScreen from './ui/ResultsScreen.js';
import GalleryScreen from './ui/GalleryScreen.js';
import ScoreStore from './services/ScoreStore.js';
import SongBestStore from './services/SongBestStore.js';
import UniverseStore from './services/UniverseStore.js';
import AuthService from './services/AuthService.js';
import AccountScreen from './ui/AccountScreen.js';
import LeaderboardOverlay from './ui/LeaderboardOverlay.js';
import RankingsScreen from './ui/RankingsScreen.js';
import { stateFromSnapshot } from './game/cosmos.js';
import { snapshotStateToDataUrl } from './game/renderer.js';
import { SONGS, loadCatalog } from './data/songs.js';
import { getDailyChallenge } from './utils/daily.js';
import { STORAGE, storageSet } from './utils/storageKeys.js';
import { buildShareCard, shareCard } from './utils/shareCard.js';

export default class App {
  constructor() {
    this.canvas = document.getElementById('game-canvas');
    this.router = new ScreenRouter();
    this.audio = new AudioManager();
    this.scores = new ScoreStore();
    this.songBest = new SongBestStore();
    this.universes = new UniverseStore();
    this.auth = new AuthService();

    this.hud = new Hud();
    this._pendingSong = null;
    this._pendingMode = 'play';
    this._pendingDifficulty = 'normal';
    this._sessionToken = null;

    this.game = new Game({
      canvas: this.canvas,
      audio: this.audio,
      hud: this.hud,
      onEnd: (result) => this._onGameEnd(result)
    });

    this.menu = new MenuScreen({
      onPlay: () => this.showCatalog(),
      onDaily: () => this.startDaily()
    });

    this.leaderboardOverlay = new LeaderboardOverlay({ auth: this.auth });

    this.catalog = new CatalogScreen({
      onBack: () => this.showMenu(),
      onSelect: (song, difficulty) => this.startPlay(song, difficulty),
      leaderboardOverlay: this.leaderboardOverlay
    });

    this.rankings = new RankingsScreen({
      onBack: () => this.showMenu(),
      overlay: this.leaderboardOverlay,
      getDifficulty: () => this.catalog.difficulty
    });

    document.getElementById('btn-rankings')?.addEventListener('click', () => {
      this.showRankings();
    });

    this.account = new AccountScreen({
      auth: this.auth,
      onBack: () => this.showMenu()
    });

    document.getElementById('btn-account')?.addEventListener('click', () => {
      this.router.show('account');
    });

    this.results = new ResultsScreen({
      onRetry: () => this._retry(),
      onMenu: () => this.showMenu(),
      onShare: () => this._shareResult()
    });
    this._lastResult = null;

    this.gallery = new GalleryScreen({
      onBack: () => this.showMenu(),
      onSelect: (universe) => this.game.startUniverseBg(universe),
      onTab: (tab) => this.showGallery(tab),
      onLike: (id) => this.auth.toggleUniverseLike(id)
    });

    document.getElementById('btn-universes')?.addEventListener('click', () => {
      this.showGallery();
    });

    document.getElementById('btn-quit')?.addEventListener('click', () => {
      this.game.quit();
    });
    document.getElementById('btn-audio')?.addEventListener('click', () => {
      this.audio.toggleMute();
    });

    this.audio.setStatusHandler(({ status, message, muted }) => {
      this.hud.setAudioState(!muted, status, message);
    });
  }

  async init() {
    this.showMenu();
    // Silent guest/session restore: never blocks the menu.
    this.auth.init().then(() => this.account.refresh());
    // Live catalog (daily VPS songs included); refreshes whatever is on screen.
    loadCatalog().then(() => {
      if (this.router.current === 'menu') this.showMenu();
      else if (this.router.current === 'catalog') this.showCatalog();
      else if (this.router.current === 'rankings') this.rankings.render(SONGS);
    });
  }

  showMenu() {
    this.game.stopLoop(false);
    this.game.startMenuBg();
    this.router.show('menu');
    this.menu.setHighScore(this.scores.getBest());
    const daily = getDailyChallenge();
    this.menu.setDailyHint(daily.song.title.replace(/ · Daily$/, ''), daily.key);
  }

  showCatalog() {
    this.catalog.render(SONGS, this.songBest.mergedWith(null));
    // Metadata-only warm-up: no track bytes are downloaded until a play starts.
    this.audio.warmSong(SONGS[0]);
    this.router.show('catalog');
    // Account scores (guests included) refresh the badges when they arrive.
    this.auth.getMyScores().then((serverScores) => {
      if (serverScores) this.catalog.setBestMap(this.songBest.mergedWith(serverScores));
    });
  }

  /** Called from the catalog tile click: trusted gesture for native audio. */
  async startPlay(song, difficulty = 'normal') {
    this.game.stopMenuBg();
    this._pendingSong = song;
    this._pendingMode = 'play';
    this._pendingDifficulty = difficulty;
    storageSet(STORAGE.LAST_MODE, 'play');
    await this._beginPlaySession(song, difficulty);
    this.router.show('hud');
    await this.game.start(song, 'play', difficulty);
  }

  async startDaily() {
    this.game.stopMenuBg();
    const daily = getDailyChallenge();
    this._pendingSong = daily.song;
    this._pendingMode = 'daily';
    this._pendingDifficulty = this.catalog.difficulty;
    storageSet(STORAGE.LAST_MODE, 'daily');
    await this._beginPlaySession(daily.song, this._pendingDifficulty);
    this.router.show('hud');
    await this.game.start(daily.song, 'daily', this._pendingDifficulty);
  }

  async _retry() {
    if (!this._pendingSong) {
      this.showMenu();
      return;
    }
    this.game.stopMenuBg();
    await this._beginPlaySession(this._pendingSong, this._pendingDifficulty || 'normal');
    this.router.show('hud');
    await this.game.start(this._pendingSong, this._pendingMode, this._pendingDifficulty || 'normal');
  }

  async _beginPlaySession(song, difficulty) {
    this._sessionToken = null;
    if (!this.auth.isOnline) return;
    const songId = song.chartId ?? song.id;
    const session = await this.auth.beginSession(songId, difficulty);
    this._sessionToken = session?.sessionToken ?? null;
  }

  showRankings() {
    this.rankings.render(SONGS);
    this.router.show('rankings');
  }

  async showGallery(tab = null) {
    this.game.stopMenuBg();
    this.router.show('gallery');
    const activeTab = tab ?? this.gallery.tab;

    let list = null;
    if (activeTab === 'mine' && !this.auth.isOnline) {
      list = this._localGalleryList();
    } else {
      const shared = await this.auth.getUniverses(activeTab);
      list = shared ?? this._localGalleryList(); // offline: local universes
    }

    this.gallery.render(list, { tab: activeTab });
    const first = list[0];
    if (first) this.game.startUniverseBg(first.data ?? first);
    else this.game.startMenuBg();
  }

  /** Local store entries shaped like the shared gallery payload. */
  _localGalleryList() {
    return this.universes.getAll().map((u) => ({
      id: null,
      name: u.name,
      era: u.era,
      data: u,
      username: null,
      likes: 0,
      likedByMe: false,
      createdAt: u.createdAt
    }));
  }

  _onGameEnd(result) {
    const { isNewRecord } = this.scores.submit(result);
    if (result.completed && result.universe) {
      // Shared gallery: every finished run publishes its universe (guests too).
      this.auth.submitUniverse({
        universe: result.universe,
        songId: this._pendingSong?.chartId ?? result.songId,
        score: result.score
      });
    }
    if (result.completed) {
      // Daily runs count on the base song (same chart) for the badges too.
      this.songBest.submit({
        songId: this._pendingSong?.chartId ?? result.songId,
        difficulty: result.difficulty,
        score: result.score,
        grade: result.grade
      });
    }
    if (result.universe) {
      const thumb = snapshotStateToDataUrl(stateFromSnapshot(result.universe));
      this.universes.submit(result.universe, thumb);
      this.game.startUniverseBg(result.universe);
    } else {
      this.game.startMenuBg();
    }
    this._lastResult = result;
    this.router.show('results');
    this.results.show(result, { isNewRecord });
    if (result.completed) this._publishScore(result);
  }

  /** Social share: image with score + song + universe + link to Orbeat. */
  async _shareResult() {
    const result = this._lastResult;
    if (!result) return;
    const song = this._pendingSong;
    const canvas = buildShareCard({
      result,
      songTitle: song?.title ?? result.songTitle,
      artistName: song?.artist ?? ''
    });
    const grade = result.grade || 'C';
    const score = Math.floor(result.score || 0).toLocaleString('fr-FR');
    const text = result.universe
      ? `Rang ${grade} · ${score} pts sur "${song?.title ?? result.songTitle}" — mon univers ${result.universe.name} est né sur Orbeat 🌌`
      : `Rang ${grade} · ${score} pts sur "${song?.title ?? result.songTitle}" — Orbeat`;
    const outcome = await shareCard(canvas, { text });
    this.results.setShareFeedback(
      outcome === 'shared'
        ? 'PARTAGÉ ✦'
        : outcome === 'downloaded'
          ? 'IMAGE TÉLÉCHARGÉE · LIEN COPIÉ'
          : 'PARTAGE IMPOSSIBLE'
    );
  }

  /** Sends the completed run to the world leaderboard, then shows the ranking. */
  async _publishScore(result) {
    // Daily plays rank on the base song (same chart, shared orbit seed).
    const songId = this._pendingSong?.chartId ?? result.songId;
    const submitted = await this.auth.submitScore({
      songId,
      difficulty: result.difficulty,
      score: result.score,
      maxCombo: result.maxCombo,
      grade: result.grade,
      sessionToken: this._sessionToken
    });
    if (!submitted.ok) return;

    const leaderboard = await this.auth.getLeaderboard(songId, result.difficulty);
    if (leaderboard) this.results.setLeaderboard(leaderboard, this.auth.user?.username);
  }
}
